<script setup>
import { useMenuStore } from '@/store/menu-store';
import { useRouterStore } from '@/store/router-store';
import { useSidebarStore } from '@/store/sidebar-store';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AppMenuItem from './AppMenuItem.vue';

const router = useRouter();
const route = useRoute();
const menuStore = useMenuStore();
const routerStore = useRouterStore();
const searchQuery = ref('');
const openTabs = computed(() => menuStore.openTabs);
const activeTab = computed(() => menuStore.activeTab);
const sidebarStore = useSidebarStore();

const handleMenuItemClick = (item) => {
    if (item.menu_item_type === 'page') {
        menuStore.addTab({
            menu_item_code: item.menu_item_code,
            menu_item_name: item.menu_item_name,
            menu_item_url: item.menu_item_url
        });

        // 작은 화면에서 메뉴 항목 클릭 후 사이드바 닫기
        if (window.innerWidth < 768) {
            sidebarStore.setSidebarState(false);
        }
    }
};

const updateTitle = () => {
    routerStore.setCurrentTitle(route.meta.title);
};

onMounted(async () => {
    await menuStore.fetchMenuItems(); // API에서 메뉴 아이템 가져오기
    updateTitle(); // 초기 로드 시 타이틀 설정
});

watch(
    () => route.path,
    () => {
        updateTitle(); // 라우트 변경 시 타이틀 업데이트
    }
);

const transformMenuItems = (items) => {
    return items.map((item) => ({
        label: item.menu_item_name,
        icon: item.icon,
        to: item.menu_item_type === 'page' ? item.menu_item_url : undefined,
        tag: item.tag,
        items: item.children_menu_items && item.menu_item_type === 'folder' ? transformMenuItems(item.children_menu_items) : undefined
    }));
};

// 검색 필터링 함수
const filterMenuItems = (items, query) => {
    return items.filter((item) => {
        const matches = item.menu_item_name.toLowerCase().includes(query.toLowerCase());
        if (item.children_menu_items && item.menu_item_type === 'folder') {
            const childMatches = filterMenuItems(item.children_menu_items, query);
            return matches || childMatches.length > 0;
        }
        return matches;
    });
};

// model 컴퓨티드 수정
const model = computed(() => {
    const rootItems = menuStore.menuItems.filter((item) => !item.parent_code);
    const filtered = searchQuery.value ? filterMenuItems(rootItems, searchQuery.value) : rootItems;

    return filtered.map((item) => ({
        items: [transformMenuItems([item])[0]]
    }));
});
</script>

<template>
    <div>
        <div class="search-container p-2">
            <InputText v-model="searchQuery" placeholder="메뉴 검색..." class="w-full custom-text" />
        </div>
        <ul class="layout-menu">
            <template v-for="(category, i) in model" :key="i">
                <app-menu-item v-if="!category.separator" :item="category" :index="i" :search-query="searchQuery"></app-menu-item>
                <li v-if="category.separator" class="menu-separator"></li>
            </template>

            <div v-if="model.length === 0 && searchQuery" class="p-2 text-color-secondary custom-text">검색 결과가 없습니다.</div>
        </ul>
    </div>
</template>

<style lang="scss" scoped>
.custom-text {
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    background-color: transparent;
}

.search-container {
    border-bottom: 0.1rem solid var(--surface-border);
}

.highlight {
    background-color: yellow;
    font-weight: bold;
}
</style>
